import { ensure, hasValue, utc } from '@vrplatform/utils';
import type { tenant_bool_exp } from '@vrplatform/graphql';
import {
  GL_HYPERLINE_DEFAULT_PLAN_NAME,
  GL_HYPERLINE_PARTNER_PLAN_NAME,
  HYPERLINE_DEFAULT_PLAN_NAME,
  HYPERLINE_PARTNER_PLAN_NAME,
} from '@vrplatform/hyperline-client';
import { z } from 'zod';
import { VRP_TENANT_ID } from '../consts';
import type { Dashboard } from '../types';

const tenantsFilterInput = {
  search: z.string().nullish(),
  tenantId: z.string().nullish(),
  featureIds: z.string().nullish().array().nullish(),
  billingPartnerId: z.string().nullish(),
  type: z.enum(['propertyManager', 'partner']).nullish(),
  product: z.enum(['vrtrust', 'vrintegrations']).nullish(),
  paymentMethod: z
    .enum(['card', 'direct_debit', 'direct_debit_ach', 'external', 'missing'])
    .nullish(),
  billingSubscriptionStatus: z
    .enum([
      'active',
      'pending',
      'paused',
      'cancelled',
      'pendingCancellation',
      'draft',
      'voided',
      'errored',
      'missing',
      'viaPartner',
    ])
    .nullish(),
  billingPlan: z
    .enum([
      GL_HYPERLINE_DEFAULT_PLAN_NAME,
      HYPERLINE_DEFAULT_PLAN_NAME,
      GL_HYPERLINE_PARTNER_PLAN_NAME,
      HYPERLINE_PARTNER_PLAN_NAME,
      'other',
      'none',
    ])
    .nullish(),
  status: z.enum(['active', 'inactive', 'onboarding']).nullish(),
};

const tenantsFilter = z.object(tenantsFilterInput);

export type TenantsFilterParams = z.infer<typeof tenantsFilter>;

type BaseWhere = {
  partnerId: string | undefined | null;
  dashboard: Dashboard;
};

export function whereTenants({
  search: s,
  partnerId,
  status,
  dashboard,
  billingPartnerId,
  billingSubscriptionStatus,
  billingPlan,
  tenantId,
  type,
  featureIds,
  product,
  paymentMethod,
}: BaseWhere & TenantsFilterParams) {
  const isOverviewDashboard = dashboard === 'overview';
  const search = s?.trim();
  const searchQuery = search
    ? ensure<tenant_bool_exp[]>([
        {
          name: { _ilike: `%${search}%` },
        },
        {
          companyName: { _ilike: `%${search}%` },
        },
      ])
    : undefined;

  const fIds = featureIds?.filter(hasValue) ?? [];

  return ensure<tenant_bool_exp>({
    id: tenantId ? { _eq: tenantId } : { _neq: VRP_TENANT_ID },
    partnerId: isOverviewDashboard
      ? undefined
      : partnerId
        ? { _eq: partnerId }
        : undefined,
    status: status !== 'onboarding' && status ? { _eq: status } : undefined,
    enabledFeatures: fIds.length ? { featureId: { _in: fIds } } : undefined,
    billingSubscriptionStatus: billingSubscriptionStatus
      ? billingSubscriptionStatus === 'missing' ||
        billingSubscriptionStatus === 'viaPartner'
        ? { _is_null: true }
        : billingSubscriptionStatus === 'pendingCancellation'
          ? { _eq: 'active' }
          : { _eq: billingSubscriptionStatus }
      : undefined,
    billingPlan: billingPlan
      ? billingPlan === 'other'
        ? {
            _nin: [
              GL_HYPERLINE_DEFAULT_PLAN_NAME,
              HYPERLINE_DEFAULT_PLAN_NAME,
              GL_HYPERLINE_PARTNER_PLAN_NAME,
              HYPERLINE_PARTNER_PLAN_NAME,
            ],
            _is_null: false,
          }
        : { _eq: billingPlan }
      : undefined,
    billingPaymentMethodType:
      paymentMethod === 'missing'
        ? { _is_null: true }
        : paymentMethod
          ? { _eq: paymentMethod }
          : undefined,
    isOnboarding:
      status === 'onboarding' || status === 'active'
        ? { _eq: status === 'onboarding' }
        : undefined,
    cancelledAt:
      billingSubscriptionStatus === 'pendingCancellation'
        ? { _gt: utc().yyyymmdd() }
        : billingSubscriptionStatus === 'active'
          ? { _is_null: true }
          : undefined,
    type: type ? { _eq: type } : { _nin: ['partner', 'admin'] },
    isGeneralLedger: product
      ? {
          _eq: product === 'vrtrust',
        }
      : undefined,
    _or: searchQuery,
    _and: ensure<(tenant_bool_exp | undefined)[]>([
      billingPartnerId
        ? { calculatedBillingPartnerId: { _eq: billingPartnerId } }
        : undefined,
      billingSubscriptionStatus
        ? {
            calculatedIsBillable: {
              _eq: billingSubscriptionStatus !== 'viaPartner',
            },
          }
        : undefined,
    ]).filter(hasValue),
  });
}
